import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Mail, Zap, ShoppingBasket, Package, Pill, UtensilsCrossed, Award, User, Building2, BadgeCheck } from "lucide-react";

const C = {
  primary:"#f97316", dark:"#ea580c", light:"#fff7ed",
  white:"#ffffff", bg:"#f2f2f7", text:"#1c1c1e", sub:"#6b7280",
  border:"#e5e7eb",
};

export default function AboutUs() {
  const navigate = useNavigate();
  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif", background:C.bg, minHeight:"100vh", color:C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}`}</style>

      {/* Header */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"14px 20px", display:"flex", alignItems:"center", gap:12, position:"sticky", top:0, zIndex:10 }}>
        <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", display:"flex" }}>
          <ArrowLeft size={22} color={C.text}/>
        </button>
        <div style={{ fontFamily:"'Fraunces',serif", fontWeight:900, fontSize:"1.15rem" }}>About Us</div>
      </div>

      <div style={{ maxWidth:640, margin:"0 auto", padding:"24px 20px 48px" }}>

        {/* Hero */}
        <div style={{ background:`linear-gradient(135deg,${C.dark},${C.primary})`, borderRadius:20, padding:"32px 24px", textAlign:"center", marginBottom:24 }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontWeight:900, fontSize:"2.8rem", color:"#fff", letterSpacing:"-1px", marginBottom:6 }}>
            Quick<span style={{ color:"#fbbf24" }}>ry</span>
          </div>
          <div style={{ color:"rgba(255,255,255,0.75)", fontSize:"0.78rem", fontWeight:600, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:12 }}>
            by Suhaniya Venture
          </div>
          <div style={{ color:"rgba(255,255,255,0.9)", fontSize:"0.92rem", fontWeight:500, lineHeight:1.7, maxWidth:340, margin:"0 auto" }}>
            Akola's first hyperlocal delivery startup — Food, Grocery, Courier & Medicine, sab ek jagah!
          </div>
          <div style={{ marginTop:16, display:"flex", justifyContent:"center", gap:10, flexWrap:"wrap" }}>
            {["Food","Grocery","Courier","Medicine"].map(s => (
              <span key={s} style={{ background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:"0.72rem", fontWeight:700, padding:"4px 12px", borderRadius:100 }}>{s}</span>
            ))}
          </div>
        </div>

        {/* Our Story */}
        <div style={{ background:C.white, borderRadius:16, padding:"20px", marginBottom:14, border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontWeight:800, fontSize:"1.1rem", marginBottom:10 }}>Our Story</div>
          <p style={{ fontSize:"0.88rem", color:C.sub, lineHeight:1.85 }}>
            Quickry was born out of a simple vision — to make everyday essentials accessible to every resident of Akola with just a few taps. Founded by <strong style={{ color:C.text }}>Rushikesh Dilip Adhav</strong>, Quickry is Akola's homegrown hyperlocal delivery startup that connects customers with local restaurants, grocery stores, and service providers.
          </p>
          <p style={{ fontSize:"0.88rem", color:C.sub, lineHeight:1.85, marginTop:10 }}>
            We believe in empowering local businesses — giving them a digital platform to reach more customers — while providing residents with fast, reliable, and affordable delivery services right at their doorstep.
          </p>
          <p style={{ fontSize:"0.88rem", color:C.sub, lineHeight:1.85, marginTop:10 }}>
            Quickry is not just an app — it's a movement to digitise and strengthen the local economy of Akola, one delivery at a time.
          </p>
        </div>

        {/* Services */}
        <div style={{ background:C.white, borderRadius:16, padding:"20px", marginBottom:14, border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontWeight:800, fontSize:"1.1rem", marginBottom:14 }}>What We Offer</div>
          {[
            { Icon:UtensilsCrossed, title:"Food Delivery",     desc:"Hot meals from Akola's best restaurants in 30–45 minutes.",          color:"#ea580c", bg:"#fff7ed" },
            { Icon:ShoppingBasket,  title:"Grocery Delivery",  desc:"Fresh groceries & daily essentials delivered in as fast as 10 min.", color:"#16a34a", bg:"#f0fdf4" },
            { Icon:Package,         title:"Courier & Pickup",  desc:"Same-day parcel delivery and pickup across Akola.",                  color:"#2563eb", bg:"#eff6ff" },
            { Icon:Pill,            title:"Medicine Delivery", desc:"Essential medicines from local pharmacies, delivered quickly.",       color:"#7c3aed", bg:"#f5f3ff" },
          ].map(({ Icon, title, desc, color, bg }) => (
            <div key={title} style={{ display:"flex", gap:14, marginBottom:14, alignItems:"flex-start" }}>
              <div style={{ width:42, height:42, borderRadius:12, background:bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <Icon size={20} color={color}/>
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:"0.88rem", marginBottom:3 }}>{title}</div>
                <div style={{ fontSize:"0.8rem", color:C.sub, lineHeight:1.6 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Founder */}
        <div style={{ background:C.white, borderRadius:16, padding:"20px", marginBottom:14, border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontWeight:800, fontSize:"1.1rem", marginBottom:14 }}>Founder</div>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:56, height:56, borderRadius:"50%", background:`linear-gradient(135deg,${C.dark},${C.primary})`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ color:"#fff", fontWeight:900, fontSize:"1.4rem", fontFamily:"'Fraunces',serif" }}>R</span>
            </div>
            <div>
              <div style={{ fontWeight:800, fontSize:"1rem", color:C.text }}>Rushikesh Dilip Adhav</div>
              <div style={{ fontSize:"0.78rem", color:C.sub, marginTop:3 }}>Founder & CEO, Quickry</div>
              <div style={{ fontSize:"0.75rem", color:C.primary, fontWeight:600, marginTop:2 }}>Proprietor, Suhaniya Venture</div>
            </div>
          </div>
        </div>

        {/* Legal Entity */}
        <div style={{ background:C.white, borderRadius:16, padding:"20px", marginBottom:14, border:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
            <Building2 size={18} color={C.primary}/>
            <div style={{ fontFamily:"'Fraunces',serif", fontWeight:800, fontSize:"1.1rem" }}>Legal Information</div>
          </div>
          {[
            { label:"Legal Entity Name",    value:"Suhaniya Venture"              },
            { label:"Brand Name",           value:"Quickry"                        },
            { label:"Business Type",        value:"Proprietorship Firm"            },
            { label:"Proprietor",           value:"Rushikesh Dilip Adhav"          },
            { label:"UDYAM Registration",   value:"UDYAM-MH-02-0046405"           },
            { label:"Business Category",    value:"Hyperlocal Delivery Platform"   },
            { label:"Registered State",     value:"Maharashtra, India"             },
            { label:"Service City",         value:"Akola, Maharashtra — 444001"    },
          ].map(({ label, value }) => (
            <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:"0.8rem", color:C.sub, fontWeight:600 }}>{label}</span>
              <span style={{ fontSize:"0.8rem", fontWeight:700, textAlign:"right", maxWidth:"55%", color:label === "UDYAM Registration" ? C.primary : C.text }}>{value}</span>
            </div>
          ))}
        </div>

        {/* UDYAM Badge */}
        <div style={{ background:"linear-gradient(135deg,#f0fdf4,#dcfce7)", borderRadius:16, padding:"16px 20px", marginBottom:14, border:"1px solid #bbf7d0", display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:48, height:48, borderRadius:12, background:"#16a34a", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <BadgeCheck size={24} color="#fff"/>
          </div>
          <div>
            <div style={{ fontWeight:800, fontSize:"0.9rem", color:"#15803d" }}>MSME Registered Startup</div>
            <div style={{ fontSize:"0.78rem", color:"#16a34a", marginTop:3 }}>Udyam Registration No: UDYAM-MH-02-0046405</div>
            <div style={{ fontSize:"0.72rem", color:"#6b7280", marginTop:2 }}>Ministry of MSME, Government of India</div>
          </div>
        </div>

        {/* Contact */}
        <div style={{ background:C.light, borderRadius:16, padding:"20px", border:`1px solid #fed7aa` }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontWeight:800, fontSize:"1.1rem", marginBottom:14, color:C.dark }}>Get in Touch</div>
          {[
            { Icon:Mail,   value:"hello.quickry@gmail.com",     href:"mailto:hello.quickry@gmail.com" },
            { Icon:Phone,  value:"+91 98765 43210",              href:"tel:+919876543210"              },
            { Icon:MapPin, value:"Akola, Maharashtra — 444001",  href:null                             },
          ].map(({ Icon, value, href }) => (
            <div key={value} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <Icon size={15} color={C.primary}/>
              {href
                ? <a href={href} style={{ fontSize:"0.85rem", color:C.text, textDecoration:"none", fontWeight:500 }}>{value}</a>
                : <span style={{ fontSize:"0.85rem", color:C.text }}>{value}</span>
              }
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}